'use client'
import './App.css'
import logo from './assets/wonderstood-high-resolution-logo-grayscale-transparent.png'
import Footer from './components/Footer'
import RegistrationModal from './components/RegistrationModal'
import SignInModal from './components/SignIn'
import HowItWorksModal from './components/HowItWorksModal'
import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { isNativePlatform } from './utils/platform'

// ─── Fade-up scroll reveal component ───
function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.classList.add('fade-up-hidden')
    el.style.transitionDelay = `${delay}s`
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('fade-up-visible')
          obs.disconnect()
        }
      },
      { rootMargin: '-80px' }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [delay])
  return <div ref={ref} className={className}>{children}</div>
}

export default function Landing() {
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [initialUserType, setInitialUserType] = useState<'client' | 'professional'>('client');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // On native platforms (iOS/Android), redirect to login page instead of showing landing
  useEffect(() => {
    if (isNativePlatform()) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  if (isNativePlatform()) {
    return null;
  }

  // Check for Firebase email verification parameters and redirect to MainApp
  useEffect(() => {
    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');
    const signin = searchParams.get('signin');
    const register = searchParams.get('register');

    if (mode === 'verifyEmail' && oobCode) {
      navigate(`/app?${searchParams.toString()}`);
    }
    if (signin === 'true') {
      setIsSignInModalOpen(true);
    }
    if (register === 'true') {
      setIsRegistrationModalOpen(true);
    }
  }, [searchParams, navigate]);

  const openRegistrationModal = (userType: 'client' | 'professional' = 'client') => {
    setInitialUserType(userType);
    setIsRegistrationModalOpen(true);
    navigate('/?register=true', { replace: false });
  };

  const closeRegistrationModal = () => {
    setIsRegistrationModalOpen(false);
    if (searchParams.get('register') === 'true') {
      navigate('/', { replace: true });
    }
  };

  const openSignInModal = () => {
    setIsSignInModalOpen(true);
    navigate('/?signin=true', { replace: false });
  };

  const closeSignInModal = () => {
    setIsSignInModalOpen(false);
    if (searchParams.get('signin') === 'true') {
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="relative bg-black text-white overflow-hidden">

      {/* ═══════════════ HERO SECTION ═══════════════ */}
      <section className="relative min-h-screen w-full overflow-hidden flex items-center">
        {/* Video background */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260325_120549_0cd82c36-56b3-4dd9-b190-069cfc3a623f.mp4"
          autoPlay muted loop playsInline preload="metadata"
          onError={(e) => { (e.currentTarget as HTMLVideoElement).style.display = 'none'; }}
        />
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-black/40" />
        {/* Bottom gradient fade to black */}
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-black to-transparent" />

        {/* Navigation */}
        <header className="fixed inset-x-0 top-0 z-50">
          <nav className="liquid-glass mx-4 mt-4 rounded-2xl sm:mx-6 lg:mx-8">
            <div className="flex items-center justify-between px-5 py-3 sm:px-6 sm:py-4">
              <a href="#" className="flex items-center group">
                <img
                  alt="wonderstood"
                  src={logo}
                  className="h-7 sm:h-9 w-auto brightness-0 invert transition-transform group-hover:scale-105 duration-200"
                />
              </a>
              <div className="flex items-center gap-2 sm:gap-4">
                <button
                  onClick={() => openRegistrationModal('professional')}
                  className="hidden sm:inline-block px-4 py-2.5 text-sm font-medium text-white/60 hover:text-white transition-colors duration-200 whitespace-nowrap"
                >
                  I'm an engineer
                </button>
                <button
                  onClick={openSignInModal}
                  className="px-4 py-2.5 text-sm font-medium text-white/70 hover:text-white transition-colors duration-200 whitespace-nowrap"
                >
                  Log in
                </button>
                <button
                  onClick={() => openRegistrationModal()}
                  className="liquid-glass rounded-full px-5 py-2.5 text-sm font-semibold text-white hover:scale-105 transition-all duration-200 whitespace-nowrap"
                >
                  Get Started
                </button>
              </div>
            </div>
          </nav>
        </header>

        {/* Hero Content */}
        <div className="relative z-10 w-full px-6 md:px-28 pt-28 md:pt-32 pb-32">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center gap-7">
            <FadeUp>
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-medium tracking-tight-2 leading-[1.02]">
                Wonder what it's <span className="font-serif italic font-normal">really</span> like?
              </h1>
            </FadeUp>

            <FadeUp delay={0.1}>
              <p className="text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">
                Your first contact with a company shouldn't be an HR screen. Book a 30-minute call with a real engineer already working where you want to be — ask how they got the job, what the day-to-day is actually like, get a resume review, or figure out whether to take the offer.
              </p>
            </FadeUp>

            <FadeUp delay={0.2}>
              <button
                onClick={() => openRegistrationModal('client')}
                className="group mt-2 bg-white text-black rounded-full px-8 py-4 text-sm font-semibold hover:scale-105 transition-all duration-300 inline-flex items-center gap-2"
              >
                Browse engineers
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </FadeUp>

            <FadeUp delay={0.3}>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8 text-sm">
                <div className="flex items-center gap-2 text-white/50">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>30-minute sessions</span>
                </div>
                <div className="flex items-center gap-2 text-white/50">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Built-in video call</span>
                </div>
                <div className="flex items-center gap-2 text-white/50">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>Pay per session</span>
                </div>
                <div className="flex items-center gap-2 text-white/50">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>No subscription</span>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ═══════════════ FEATURES SECTION ═══════════════ */}
      <section className="relative py-24 sm:py-32">
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">

          <FadeUp>
            <div className="max-w-2xl mb-16">
              <p className="text-xs font-semibold text-white/40 uppercase tracking-[3px] mb-4">Why Wonderstood</p>
              <h2 className="text-4xl md:text-6xl font-medium tracking-tight-1 leading-[1.05]">
                Built for the questions your career center <span className="font-serif italic font-normal">can't</span> answer
              </h2>
              <p className="mt-6 text-base text-white/50 leading-relaxed">
                Finding someone willing to do an informational interview has always been the hard part — the cold message, the follow-up, the scheduling back-and-forth. We cut all of that out.
              </p>
            </div>
          </FadeUp>

          {/* 3-column feature cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 border-t border-white/10 pt-12">

            <FadeUp delay={0}>
              <div className="flex flex-col">
                <div className="w-10 h-10 rounded-full liquid-glass flex items-center justify-center mb-5">
                  <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                  </svg>
                </div>
                <p className="text-xs font-semibold text-white/40 uppercase tracking-[3px] mb-3">For CS Students</p>
                <h3 className="text-xl font-semibold text-white mb-3 leading-snug">
                  Get the real answers no one else will give you
                </h3>
                <p className="text-sm text-white/50 leading-relaxed mb-6 flex-1">
                  What's it actually like at a startup vs. big tech? How did that ML engineer break in without a CS degree? Thirty minutes with someone doing the job beats a semester of career advice.
                </p>
                <button
                  onClick={() => openRegistrationModal('client')}
                  className="group inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors duration-200"
                >
                  Find an engineer
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="flex flex-col">
                <div className="w-10 h-10 rounded-full liquid-glass flex items-center justify-center mb-5">
                  <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-xs font-semibold text-white/40 uppercase tracking-[3px] mb-3">For Software Engineers</p>
                <h3 className="text-xl font-semibold text-white mb-3 leading-snug">
                  Get paid for the coffee chats you already do for free
                </h3>
                <p className="text-sm text-white/50 leading-relaxed mb-6 flex-1">
                  You're already answering these questions on LinkedIn for nothing. Set your rate, open your calendar as much or as little as you want, and let students book you directly.
                </p>
                <button
                  onClick={() => openRegistrationModal('professional')}
                  className="group inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors duration-200"
                >
                  Join as an engineer
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </FadeUp>

            <FadeUp delay={0.2}>
              <div className="flex flex-col">
                <div className="w-10 h-10 rounded-full liquid-glass flex items-center justify-center mb-5">
                  <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 00-3.7-3.7 48.678 48.678 0 00-7.324 0 4.006 4.006 0 00-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3l-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 003.7 3.7 48.656 48.656 0 007.324 0 4.006 4.006 0 003.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3l-3 3" />
                  </svg>
                </div>
                <p className="text-xs font-semibold text-white/40 uppercase tracking-[3px] mb-3">No friction</p>
                <h3 className="text-xl font-semibold text-white mb-3 leading-snug">
                  Show up, talk, done.
                </h3>
                <p className="text-sm text-white/50 leading-relaxed flex-1">
                  Video call is built in. Payment is automatic. Students come prepared with questions, engineers get paid the same week. Nobody's chasing anyone down.
                </p>
              </div>
            </FadeUp>

          </div>
        </div>
      </section>

      {/* ═══════════════ FINAL CTA SECTION ═══════════════ */}
      <section className="relative py-32 sm:py-44 border-t border-white/10 overflow-hidden">
        {/* Second video background */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260325_132944_a0d124bb-eaa1-4082-aa30-2310efb42b4b.mp4"
          autoPlay muted loop playsInline preload="metadata"
          onError={(e) => { (e.currentTarget as HTMLVideoElement).style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-black/50" />

        <FadeUp className="relative z-10">
          <div className="mx-auto max-w-4xl text-center px-6 lg:px-8 flex flex-col items-center gap-7">
            <p className="text-xs font-semibold text-white/40 uppercase tracking-[3px]">Get started</p>

            <h2 className="text-5xl md:text-7xl font-medium tracking-tight-2 leading-[1.02]">
              Stop wondering. Start <span className="font-serif italic font-normal">asking.</span>
            </h2>

            <p className="max-w-2xl text-base md:text-lg text-white/60 leading-relaxed">
              Pick an engineer, pick a time, and spend 30 minutes getting the clarity you've been looking for. Your career center means well — but they've never shipped production code.
            </p>

            <button
              onClick={() => openRegistrationModal('client')}
              className="group mt-4 bg-white text-black rounded-full px-8 py-4 text-sm font-semibold hover:scale-105 transition-all duration-300 inline-flex items-center gap-2"
            >
              Browse engineers
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>

            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-white/40 mt-4">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Free to browse</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>No subscription</span>
              </div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* Footer */}
      <div className="relative border-t border-white/10">
        <Footer />
      </div>

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={isRegistrationModalOpen}
        onClose={closeRegistrationModal}
        initialUserType={initialUserType}
        onSwitchToSignIn={() => {
          closeRegistrationModal();
          openSignInModal();
        }}
      />

      {/* Sign In Modal */}
      <SignInModal
        isOpen={isSignInModalOpen}
        onClose={closeSignInModal}
        onSwitchToRegister={() => {
          closeSignInModal();
          openRegistrationModal();
        }}
      />

      {/* How It Works Modal */}
      <HowItWorksModal
        isOpen={isHowItWorksModalOpen}
        onClose={() => setIsHowItWorksModalOpen(false)}
      />
    </div>
  )
}
